import React, { useEffect, useState } from 'react';
import { get } from '../utils/api';
import { SUBTYPE_LABELS } from '../constants/subtypes';

const fmt = n => new Intl.NumberFormat('ru-RU').format(Math.round(n || 0)) + ' ₽';
const fmtDate = d => { if (!d) return d; const [y, m, day] = d.split('-'); return `${day}-${m}-${y.slice(2)}`; };
const MONTHS_RU = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];

const HIDDEN_SUBTYPES = new Set(['cash_opening_adjustment', 'deposit_fund_opening', 'emergency_fund_opening', 'overnight_opening']);

const monthLabel = key => { if (!key) return key; const [y,m] = key.split('-'); return `${MONTHS_RU[parseInt(m)-1]} ${y}`; };

export default function DDSPage() {
  const now = new Date();
  const [from, setFrom] = useState(`${now.getFullYear()}-01-01`);
  const [to, setTo] = useState(`${now.getFullYear()}-12-31`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandMonth, setExpandMonth] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const d = await get(`/reports/dds?from=${from}&to=${to}`); setData(d); }
    catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [from, to]);

  const months = data ? Object.keys(data.byMonth).sort() : [];
  const totalIn = months.reduce((s, m) => s + (data.byMonth[m]?.total_in || 0), 0);
  const totalOut = months.reduce((s, m) => s + (data.byMonth[m]?.total_out || 0), 0);

  // Group cash balance
  const cashMap = {};
  (data?.cashBalance || []).forEach(r => { cashMap[r.payment_method] = r.balance; });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">ДДС <span style={{ fontSize: 14, color: 'var(--text3)' }}>Движение денежных средств</span></h1>
      </div>

      <div className="filters" style={{ marginBottom: 24 }}>
        <div className="form-group"><label>С</label><input type="date" value={from} onChange={e => setFrom(e.target.value)} /></div>
        <div className="form-group"><label>По</label><input type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          {[
            { label: String(now.getFullYear()), f: `${now.getFullYear()}-01-01`, t: `${now.getFullYear()}-12-31` },
            { label: String(now.getFullYear()-1), f: `${now.getFullYear()-1}-01-01`, t: `${now.getFullYear()-1}-12-31` },
          ].map((p, i) => (
            <button key={i} className="btn btn-ghost btn-sm" onClick={() => { setFrom(p.f); setTo(p.t); }}>{p.label}</button>
          ))}
        </div>
      </div>

      {loading && <p style={{ color: 'var(--text3)' }}>Загрузка...</p>}

      {!loading && data && (<>
        {/* Summary */}
        <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 24 }}>
          <div className="stat-card"><div className="stat-label">Поступления</div><div className="stat-value green">{fmt(totalIn)}</div></div>
          <div className="stat-card"><div className="stat-label">Выплаты</div><div className="stat-value red">{fmt(totalOut)}</div></div>
          <div className="stat-card"><div className="stat-label">Чистый поток</div><div className={`stat-value ${totalIn - totalOut >= 0 ? 'green' : 'red'}`}>{fmt(totalIn - totalOut)}</div></div>
          <div className="stat-card">
            <div className="stat-label">Остатки на счетах</div>
            <div style={{ marginTop: 4 }}>
              {Object.entries(cashMap).map(([method, bal]) => (
                <div key={method} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 2 }}>
                  <span style={{ color: 'var(--text2)' }}>{method}</span>
                  <span style={{ fontWeight: 700, color: bal >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmt(bal)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Subtotals by type */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
          <div className="card">
            <div className="section-title">Поступления по статьям</div>
            {(data.subtotals || []).filter(r => (r.type === 'income' || r.type === 'internal_income') && r.subtype !== 'deposit' && !HIDDEN_SUBTYPES.has(r.subtype)).map(r => (
              <div key={r.subtype} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 14 }}>
                <span style={{ color: 'var(--text2)' }}>{SUBTYPE_LABELS[r.subtype] || r.subtype}</span>
                <span style={{ fontWeight: 700, color: 'var(--green)' }}>{fmt(r.total)}</span>
              </div>
            ))}
          </div>
          <div className="card">
            <div className="section-title">Выплаты по статьям</div>
            {(data.subtotals || []).filter(r => (r.type === 'expense' || r.type === 'internal_expense' || r.type === 'dividend') && !HIDDEN_SUBTYPES.has(r.subtype)).map(r => (
              <div key={r.subtype} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 14 }}>
                <span style={{ color: 'var(--text2)' }}>{SUBTYPE_LABELS[r.subtype] || r.subtype}</span>
                <span style={{ fontWeight: 700, color: 'var(--red)' }}>{fmt(r.total)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly table */}
        <div className="card">
          <div className="section-title">По месяцам</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Месяц</th><th>Поступления</th><th>Выплаты</th><th>Поток</th><th>Остаток нарастающим</th><th></th></tr>
              </thead>
              <tbody>
                {months.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text3)', padding: 30 }}>Нет данных</td></tr>}
                {(() => {
                  let running = 0;
                  return months.map(m => {
                    const mData = data.byMonth[m];
                    const flow = (mData?.total_in || 0) - (mData?.total_out || 0);
                    running += flow;
                    return (
                      <React.Fragment key={m}>
                        <tr style={{ cursor: 'pointer' }} onClick={() => setExpandMonth(expandMonth === m ? null : m)}>
                          <td style={{ fontWeight: 600 }}>{monthLabel(m)}</td>
                          <td style={{ color: 'var(--green)', fontWeight: 600 }}>{fmt(mData?.total_in || 0)}</td>
                          <td style={{ color: 'var(--red)', fontWeight: 600 }}>{fmt(mData?.total_out || 0)}</td>
                          <td style={{ fontWeight: 700, color: flow >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmt(flow)}</td>
                          <td style={{ color: running >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>{fmt(running)}</td>
                          <td style={{ color: 'var(--text3)' }}>{expandMonth === m ? '▲' : '▼'}</td>
                        </tr>
                        {expandMonth === m && (
                          <tr>
                            <td colSpan={6} style={{ padding: 0 }}>
                              <div style={{ padding: '12px 16px', background: 'rgba(0,0,0,0.15)' }}>
                                <div style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>Транзакции месяца</div>
                                {[...(mData?.income || []), ...(mData?.expense || [])].slice(0, 20).map((t, i) => (
                                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
                                    <span style={{ color: 'var(--text2)' }}>{fmtDate(t.date)} · {t.tenant_name || t.utility_property || t.utility_type || t.category_name || t.description || SUBTYPE_LABELS[t.subtype] || '—'}</span>
                                    <span style={{ fontWeight: 600, color: (t.type === 'income' || t.type === 'internal_income') ? 'var(--green)' : 'var(--red)' }}>{(t.type === 'income' || t.type === 'internal_income') ? '+' : '-'}{fmt(t.amount)}</span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>
      </>)}
    </div>
  );
}
